import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ImageOff, ShoppingCart } from "lucide-react";
import { removeCartItem, updateCartItemQuantity, fetchProducts } from "../../store";
import { useNavigate } from "react-router-dom";
import { validateCartStock } from "../../utils/stockValidation";
import StockWarning from "./StockWarning";
import "./CartDetails.css";

const cartIconProps = { size: 80, strokeWidth: 1.5, "aria-hidden": true };
const cartThumbProps = { size: 64, strokeWidth: 1.5, "aria-hidden": true };

export default function CartDetails() {
  const cartItems = useSelector(state => state.cart.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(state => state.user.userId); // fetch userId from store

  const handleRemoveFromCart = (productId) => {
    dispatch(removeCartItem({ userId, productId }))
  };

  const products = useSelector(state => state.products.items);
  const productsObject = products.reduce((acc, product) => {
    acc[product._id] = product;
    return acc;
  }, {});

  useEffect(() => {
    if (userId) {
      dispatch(fetchProducts()); // Fetch products when component mounts
    }
  }, [userId, dispatch]);

  // Validate cart stock whenever cart items or products change
  useEffect(() => {
    if (cartItems.length > 0 && products.length > 0) {
      const validation = validateCartStock(cartItems, products);
      setStockValidation(validation);
    } else {
      setStockValidation({ valid: true, issues: [] });
    }
  }, [cartItems, products]);

  // For quantity input edit mode
  const [editQty, setEditQty] = useState({});
  const [inputValue, setInputValue] = useState({});

  // Stock validation state
  const [stockValidation, setStockValidation] = useState({ valid: true, issues: [] });

  // Helper to get MOQ for a product (default 1)
  const getMOQ = (product) => {
    return productsObject[product.product]?.moq ? Number(productsObject[product.product]?.moq) : 100;
  };

  const DEFAULT_TAX_PCT = 18;
  const taxPctForProductId = (productId) => {
    const p = productsObject[productId];
    const n = Number(p?.taxPercentage);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : DEFAULT_TAX_PCT;
  };

  const getSubtotalNum = () =>
    cartItems.reduce(
      (sum, item) =>
        sum +
        (productsObject[item.product]?.price?.amount
          ? Number(productsObject[item.product].price.amount) * item.quantity
          : 0),
      0
    );

  const getTaxNum = () =>
    +cartItems
      .reduce((sum, item) => {
        const unit = productsObject[item.product]?.price?.amount;
        const line =
          unit != null ? Number(unit) * item.quantity : 0;
        return sum + line * (taxPctForProductId(item.product) / 100);
      }, 0)
      .toFixed(2);

  const getGrandTotalNum = () =>
    +(getSubtotalNum() + Number(getTaxNum())).toFixed(2);

  const getItemCount = () => cartItems.reduce((count, item) => count + 1, 0);

  // Check if cart is empty
  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="cart-main-container">
        {/* <div className="page-header">
          <h1 className="page-title">Shopping Cart</h1>
          <p className="page-subtitle">Review your selected paper bags before checkout</p>
        </div> */}

        <div className="empty-cart">
          <div className="empty-cart-icon">
            <ShoppingCart {...cartIconProps} />
          </div>
          <h2 className="empty-cart-message">Your cart is empty</h2>
          <p className="empty-cart-sub">Looks like you haven't added any items to your cart yet.</p>
          <button
            className="continue-shopping-empty"
            onClick={() => navigate('/')}
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-main-container">
      <div className="page-header">
        <h1 className="page-title">Shopping Cart</h1>
        <p className="page-subtitle">Review your selected paper bags before checkout</p>
      </div>

      {/* Stock Warning - Show if there are validation issues */}
      {!stockValidation.valid && (
        <StockWarning
          issues={stockValidation.issues}
          onRemoveItems={(productId) => handleRemoveFromCart(productId)}
        />
      )}

      <div className="page-header-hidden">
        <h1 className="page-title-hidden">Shopping Cart</h1>
        <p className="page-subtitle-hidden">Review your selected paper bags before checkout</p>
      </div>

      <div className="cart-container">
        <div className="cart-items">
          {cartItems.map((item, idx) => {
            const productObj = productsObject[item.product._id] || productsObject[item.product];
            if (!productObj) return null; // <-- Skip if product not found

            const moq = getMOQ(item);
            const isEditing = editQty[productObj._id];
            return (
              <div className="cart-item" key={productObj._id || idx}>
                <div className="cart-product-image">
                  {productObj.images[0] ? (
                    <img src={productObj.images[0]} alt={productObj.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  ) : (
                    <span className="cart-product-image-placeholder" aria-hidden>
                      <ImageOff {...cartThumbProps} />
                    </span>
                  )}
                </div>

                <div className="cart-product-details">
                  <h3 className="cart-product-name">{productObj.name}</h3>
                  <p className="cart-product-description">{productObj.description}</p>
                  <div className="cart-product-specs">
                    <div className="cart-spec-item">
                      <span className="cart-spec-label">Size:</span>
                      <span className="cart-spec-value">{productObj.bagSize.cm}</span>
                    </div>
                    <div className="cart-spec-item">
                      <span className="cart-spec-label">GSM:</span>
                      <span className="cart-spec-value">{productObj.specifications.gsm}</span>
                    </div>
                    <div className="cart-spec-item">
                      <span className="cart-spec-label">BF:</span>
                      <span className="cart-spec-value">{productObj.specifications.bf}</span>
                    </div>
                    <div className="cart-spec-item">
                      <span className="cart-spec-label">Material:</span>
                      <span className="cart-spec-value">{productObj.material}</span>
                    </div>
                    <div className="cart-spec-item">
                      <span className="cart-spec-label">Minimum Order Quantity:</span>
                      <span className="cart-spec-value">{productObj.moq}</span>
                    </div>
                    <div className="cart-spec-item">
                      <span className="cart-spec-label">Unit Price:</span>
                      <span className="cart-spec-value">{`${productObj.price?.currency} ` || "$ "}
                        {productObj.price?.amount
                          ? (Number(productObj.price.amount)).toFixed(2)
                          : "0.00"}</span>
                    </div>
                  </div>
                </div>
                <div className="cart-product-actions">
                  <div className="cart-quantity-controls">
                    <button
                      className="cart-quantity-btn"
                      onClick={() => {
                        const productId = productObj;
                        const newQuantity = item.quantity - 1;
                        if (newQuantity >= moq) {
                          dispatch(updateCartItemQuantity({
                            userId,
                            product: productId,
                            quantity: newQuantity
                          }));
                        }
                      }}
                      disabled={item.quantity <= moq}
                    >
                      −
                    </button>
                    {isEditing ? (
                      <input
                        type="number"
                        className="cart-quantity-input editing"
                        value={inputValue[productObj._id]}
                        min={moq}
                        autoFocus
                        onChange={e => {
                          const val = e.target.value;
                          setInputValue(prev => ({ ...prev, [productObj._id]: val.replace(/[^\d]/g, "") }));
                        }}
                        onBlur={() => {
                          let val = parseInt(inputValue[productObj._id], 10);
                          if (val >= moq) {
                            dispatch(updateCartItemQuantity({
                              userId,
                              product: productObj,
                              quantity: val
                            }));
                          }
                          if (isNaN(val) || val < moq) val = moq;
                          setEditQty(prev => ({ ...prev, [productObj._id]: false }));
                        }}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            let val = parseInt(inputValue[productObj._id], 10);
                            if (val >= moq) {
                              dispatch(updateCartItemQuantity({
                                userId,
                                product: productObj._id,
                                quantity: val
                              }));
                            }
                            if (isNaN(val) || val < moq) val = moq;
                            setEditQty(prev => ({ ...prev, [productObj._id]: false }));
                          } else if (e.key === "Escape") {
                            setEditQty(prev => ({ ...prev, [productObj._id]: false }));
                          }
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        className="cart-quantity-input"
                        value={item.quantity}
                        readOnly
                        onClick={() => {
                          setEditQty(prev => ({ ...prev, [productObj._id]: true }));
                          setInputValue(prev => ({ ...prev, [productObj._id]: item.quantity }));
                        }}
                      />
                    )}
                    <button
                      className="cart-quantity-btn"
                      onClick={() => {
                        const product = productObj;
                        const availableStock = productObj.stock || 0;
                        const newQuantity = item.quantity + 1;

                        // Check if we can increment based on available stock
                        if (newQuantity <= availableStock) {
                          dispatch(updateCartItemQuantity({
                            userId,
                            product: product, // pass product ID, not the whole object
                            quantity: newQuantity
                          }));
                        }
                      }}
                      disabled={item.quantity >= (productObj.stock || 0)}
                      title={item.quantity >= (productObj.stock || 0) ? "Maximum available stock reached" : "Increase quantity"}
                    >
                      +
                    </button>
                  </div>
                  <div className="cart-product-price">
                    {`${productObj.price?.currency} ` || "$ "}
                    {productObj.price?.amount
                      ? (Number(productObj.price.amount) * item.quantity).toFixed(2)
                      : "0.00"}
                  </div>
                  <button
                    className="remove-btn"
                    onClick={() => handleRemoveFromCart(productObj._id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="cart-summary">
          <h3 className="summary-title">Cart Summary</h3>

          <div className="summary-row">
            <span className="summary-label">Subtotal ({getItemCount()} items)</span>
            <span className="summary-value">Rs {getSubtotalNum().toFixed(2)}</span>
          </div>

          <div className="summary-row">
            <span className="summary-label">Tax (per product)</span>
            <span className="summary-value">Rs {Number(getTaxNum()).toFixed(2)}</span>
          </div>

          <div className="summary-row summary-total">
            <span className="total-label">Total</span>
            <span className="total-value">Rs {getGrandTotalNum().toFixed(2)}</span>
          </div>

          <button
            className="checkout-btn"
            onClick={() => navigate('/orderdetails')}
            disabled={!stockValidation.valid}
            title={!stockValidation.valid ? "Please resolve stock issues before checkout" : "Proceed to checkout"}
          >
            Proceed to Checkout
          </button>
          <button className="continue-shopping" onClick={() => navigate('/')}>Continue Shopping</button>

          {/* <div className="promo-section"></div>
            <input type="text" className="promo-input" placeholder="Enter promo code" />
            <button className="apply-promo">Apply Code</button>
          </div> */}
        </div>
      </div>
    </div >
  );
}